import { useState } from "react";

import {
  useAdminPatient,
  useAdminPatientUser,
} from "../../../../hooks/queries/admin/patients";
import { useModal } from "../../../../hooks/useModal";
import {
  ErrorMessage,
  FilterButton,
  LoadingOverlay,
  Tab,
  TabsContainer,
  WarningNotice,
} from "../AdminPage.styles";
import { ChangeWardModal } from "./ChangeWardModal";
import { PatientClinicalTab } from "./PatientClinicalTab";
import { PanelStack } from "./PatientDetailsPanel.styles";
import { PatientHistoryTab } from "./PatientHistoryTab";
import { PatientMonitoringTab } from "./PatientMonitoringTab";
import { PatientRecordForm } from "./PatientRecordForm";
import { PatientUserForm } from "./PatientUserForm";

export type IPatientDetailTab =
  | "record"
  | "clinical"
  | "monitoring"
  | "history";

const TABS: { id: IPatientDetailTab; label: string }[] = [
  { id: "record", label: "Record" },
  { id: "clinical", label: "Clinical" },
  { id: "monitoring", label: "Monitoring" },
  { id: "history", label: "History" },
];

export interface IPatientDetailsPanelProps {
  patientId: number;
  /** Which tab opens first. Defaults to Record. */
  initialTab?: IPatientDetailTab;
}

/**
 * One patient's record, account, clinical data, monitoring and audit trails —
 * the portal port of Retool's `PatientDetails` screen (EN-5169 + EN-5170).
 *
 * ⚠️ The most sensitive component in the /admin section. Behind these tabs an
 * operator can edit the clinical record, move the patient between wards, change
 * their condition and reset their password. Every one of those is behind a
 * confirmation that names the patient and says what will happen.
 *
 * **Four tabs, not thirteen stacked containers.** Retool put the two forms and
 * eleven panels on a single screen, so the patient's own identity scrolled off
 * the top long before the last table. The tabs group by the question being
 * asked, and the summary strip below keeps the identifiers on screen whichever
 * tab is open:
 *
 *   Record      the patient record, the account, the ward set
 *   Clinical    conditions, measurements, baselines, medications
 *   Monitoring  protocol actions, workflows, alerts, devices, surveys,
 *               treatments
 *   History     the patient / user / condition audit trails
 *
 * Shared rather than page-local because the Archived Patients page (EN-5171)
 * shows the same detail view — `page_ArchivedPatients` has its own copy of this
 * screen in the Retool export.
 *
 * **Embed it inline, never inside the global modal**: the audit panels and the
 * measurement drill-down open their own dialogs there.
 */
export function PatientDetailsPanel({
  patientId,
  initialTab = "record",
}: IPatientDetailsPanelProps) {
  const [tab, setTab] = useState<IPatientDetailTab>(initialTab);
  const { openModal, closeModal } = useModal();

  const { patient, isLoading, isError } = useAdminPatient(patientId);
  const { user, isLoading: isUserLoading } = useAdminPatientUser(
    patient?.userId
  );

  if (isLoading) {
    return <LoadingOverlay>Loading patient...</LoadingOverlay>;
  }

  if (isError || !patient) {
    return (
      <ErrorMessage>
        Failed to load this patient. Try refreshing the page.
      </ErrorMessage>
    );
  }

  return (
    <PanelStack>
      {/* Said once, at the top, rather than left to the operator noticing the
          State field or the missing buttons. An archived patient is reachable
          from the live Patients list too — that list does not filter on
          archived — so this cannot live on the Archived Patients page. */}
      {patient.archived && (
        <WarningNotice>
          This patient is archived (discharged). Their record is still editable,
          but password reset, the enrolment email, changing the sign-in address
          and eMED registration are unavailable until they are un-archived —
          which is the Archived checkbox on the Record tab.
        </WarningNotice>
      )}

      <TabsContainer>
        {TABS.map((entry) => (
          <Tab
            key={entry.id}
            type="button"
            active={tab === entry.id}
            onClick={() => setTab(entry.id)}
          >
            {entry.label}
          </Tab>
        ))}
      </TabsContainer>

      {tab === "record" && (
        <PanelStack>
          <PatientRecordForm
            patient={patient}
            wardAction={
              <FilterButton
                variant="secondary"
                onClick={() =>
                  openModal(
                    <ChangeWardModal patient={patient} onDone={closeModal} />,
                    {
                      width: "min(600px, 94vw)",
                      maxHeight: "90vh",
                      showCloseButton: true,
                    }
                  )
                }
              >
                Change wards
              </FilterButton>
            }
          />
          {isUserLoading && (
            <LoadingOverlay>Loading the linked user account...</LoadingOverlay>
          )}
          {!isUserLoading && user && (
            <PatientUserForm patient={patient} user={user} />
          )}
        </PanelStack>
      )}

      {tab === "clinical" && <PatientClinicalTab patient={patient} />}
      {tab === "monitoring" && <PatientMonitoringTab patient={patient} />}
      {tab === "history" && <PatientHistoryTab patient={patient} />}
    </PanelStack>
  );
}
